import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { ApiService } from 'src/app/services';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { WorkingContractCareerPlan } from 'src/app/models/working_contract_career_plan.model';
import { WorkingContractCareerPlanYear } from 'src/app/models/working_contract_career_plan_year.model';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-career-plan-years-list-dialog',
  templateUrl: './career-plan-years-list-dialog.component.html',
  styleUrls: ['./career-plan-years-list-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatIconModule,
    MatTooltip,
  ],
})
export class CareerPlanYearsListDialogComponent implements OnInit, AfterViewInit {
  dialogRef = inject<MatDialogRef<CareerPlanYearsListDialogComponent>>(MatDialogRef);
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  data = inject<{
    workingContractCareerPlan: WorkingContractCareerPlan;
  }>(MAT_DIALOG_DATA);

  displayedColumns: string[] = ['year', 'department_categories', 'custom_brute_anual_base_salary', 'custom_weekly_hours', 'actions'];
  dataSource = new MatTableDataSource<WorkingContractCareerPlanYearCustom>([]);
  workingContractCareerPlan: WorkingContractCareerPlan;
  departmentCategories: DepartmentCategory[];
  readonly currentYear: number = new Date().getFullYear();
  editingYearId: number | null = null;
  editForm: FormGroup;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor() {
    this.editForm = this.fb.group({
      department_categories_ids: [[], Validators.required],
      custom_brute_anual_base_salary: [null],
      custom_weekly_hours: [null, [Validators.required, Validators.min(0.01)]],
      use_default_salary: [true],
      use_default_hours: [true],
    });
  }

  ngOnInit(): void {
    if (this.data.workingContractCareerPlan.is_active || this.data.workingContractCareerPlan.is_past) {
      this.displayedColumns = this.displayedColumns.filter((col: string): boolean => col !== 'actions');
    }

    this.fetchWorkingContractCareerPlanYears();
    this.fetchDepartmentCategories();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  onEditUseDefaultSalaryChange(useDefault: boolean): void {
    if (useDefault) {
      this.editForm.get('custom_brute_anual_base_salary')?.disable();
      this.editForm.get('custom_brute_anual_base_salary')?.setValue(null);
    } else {
      this.editForm.get('custom_brute_anual_base_salary')?.enable();
    }
  }

  onEditUseDefaultHoursChange(useDefault: boolean): void {
    if (useDefault) {
      this.editForm.get('custom_weekly_hours')?.disable();
      this.editForm.get('custom_weekly_hours')?.setValue(null);
    } else {
      this.editForm.get('custom_weekly_hours')?.enable();
    }
  }

  startEdit(year: WorkingContractCareerPlanYear): void {
    this.editingYearId = year.id;

    this.editForm.patchValue({
      department_categories_ids: year.department_categories.map(cat => cat.id),
      custom_brute_anual_base_salary: year.custom_brute_anual_base_salary,
      custom_weekly_hours: year.custom_weekly_hours,
      use_default_salary: !year.custom_brute_anual_base_salary,
      use_default_hours: !year.custom_weekly_hours,
    });

    this.onEditUseDefaultSalaryChange(!year.custom_brute_anual_base_salary);
    this.onEditUseDefaultHoursChange(!year.custom_weekly_hours);
  }

  saveEdit(workingContractCareerPlanYear: WorkingContractCareerPlanYear): void {
    if (this.editForm.valid) {
      const formValue = this.editForm.value;

      const body = {
        department_categories_ids: formValue.department_categories_ids,
        custom_brute_anual_base_salary: formValue.use_default_salary ? null : formValue.custom_brute_anual_base_salary,
        custom_weekly_hours: formValue.use_default_hours ? null : formValue.custom_weekly_hours,
        should_propagate_to_next_years: confirm('¿Quieres propagar los cambios a los años siguientes?'),
      };

      this.api.updateWorkingContractCareerPlanYear(workingContractCareerPlanYear.id, body).subscribe((): void => {
        this.editingYearId = null;
        this.fetchWorkingContractCareerPlanYears();
      });
    }
  }

  cancelEdit(): void {
    this.editingYearId = null;
  }

  private fetchWorkingContractCareerPlanYears(): void {
    this.api
      .getWorkingContractCareerPlanYearsFrom(this.data.workingContractCareerPlan.id)
      .subscribe((data: WorkingContractCareerPlan): void => {
        this.workingContractCareerPlan = data;

        this.dataSource.data = data.working_contract_career_plan_years.map(
          (year: WorkingContractCareerPlanYear): WorkingContractCareerPlanYearCustom => ({
            ...year,
            custom_weekly_hours_str: year.custom_weekly_hours ? this.formatWeeklyHours(year.custom_weekly_hours) : null,
            default_hours_per_week_expected_str: year.default_hours_per_week_expected
              ? this.formatWeeklyHours(year.default_hours_per_week_expected)
              : null,
          })
        );
      });
  }

  private fetchDepartmentCategories(): void {
    this.api.getDepartmentCategories().subscribe((response: ArrayResponse<DepartmentCategory>): void => {
      this.departmentCategories = response.data;
    });
  }

  private formatWeeklyHours(hours: number): string {
    const h = Math.floor(hours);
    const min = Math.round((hours - h) * 60);

    return min > 0 ? `${h}h ${min}min` : `${h}h`;
  }
}

interface WorkingContractCareerPlanYearCustom extends WorkingContractCareerPlanYear {
  custom_weekly_hours_str: string | null;
  default_hours_per_week_expected_str: string | null;
}
